// Ported from eb-ui `src/legacy/components/Share/ShareCollection.tsx`.
//   - `@eventbrite/legacy-infra.i18n` → `@/lib/gettext`.
//   - `@eventbrite/legacy-infra.transformation-utils` `keysCamelToSnake` →
//     inlined below (~5-line lowercase-camel→snake_case).
//   - `@eventbrite/social-share` → the vendored shared module (same
//     `ShareInline`/`ShareModal`/`ShareCollectionProps` contract).
// The share URLs (the wire) are preserved byte-for-byte.
import {
    ShareCollectionProps,
    ShareInline,
    ShareModal,
} from '@eventbrite/social-share';
import React from 'react';
import { formatUrl } from 'url-lib';

import { AffiliateCodes } from '@/types';
import { gettext } from '@/lib/gettext';

/**
 * `keysCamelToSnake` (legacy-infra.transformation-utils) — recursively
 * lowercase-camel→snake_case the KEYS of a plain object. Only key names are
 * transformed (values are the aff code / utm literals, already snake).
 */
type UrlParamValue =
    | string
    | number
    | boolean
    | object
    | null
    | undefined;

const keysCamelToSnake = (obj: Record<string, UrlParamValue>): Record<
    string,
    UrlParamValue
> => {
    const out: Record<string, UrlParamValue> = {};
    Object.keys(obj).forEach((key) => {
        const snakeKey = key.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
        out[snakeKey] = obj[key];
    });
    return out;
};

type Props = ShareCollectionProps & {
    container: React.MutableRefObject<HTMLDivElement | null>;
};

export const ShareCollectionModal: React.FC<Props> = (props) => {
    const {
        collectionName,
        collectionUrl,
        container,
        isMobile,
        trackingCategory,
    } = props;

    const utmOptions = {
        'utm-campaign': 'social',
        'utm-content': 'attendeeshare',
        'utm-medium': 'discovery',
        'utm-term': AffiliateCodes.SocialShareToolsInCollection,
    };

    const dataWebShareApi = {
        text: collectionName,
        title: collectionName,
        url: formatUrl(
            collectionUrl,
            keysCamelToSnake({
                ...utmOptions,
                'utm-source': 'wsa',
                aff: AffiliateCodes.SocialShareToolsInCollection,
            }),
        ),
    };
    const urlCopyField = formatUrl(
        collectionUrl,
        keysCamelToSnake({
            ...utmOptions,
            'utm-source': 'cp',
            aff: AffiliateCodes.SocialShareToolsInCollection,
        }),
    );
    return (
        <ShareModal
            dataWebShareApi={dataWebShareApi}
            title={gettext('Share with friends').toString()}
            inputLabel={gettext('Collection link').toString()}
            url={urlCopyField}
            container={container}
            tooltipTitle={gettext('Share this Collection')}
            tooltipText={gettext('Share collection')}
            trackingCategory={trackingCategory}
            isMobile={isMobile}
            useWebShareApi={true}
        >
            <ShareInline {...props} utmOptions={utmOptions} />
        </ShareModal>
    );
};

export const ShareCollectionInline = ShareInline;
